import { applyHeroVideoPoster, applyHeroVideoSource, selectHeroVideoSource } from "@/utils/hero-video.util";
export class LoadingImage {
    constructor(rootElement) {
        this.previewObserver = null;
        this.settings = {
            previewClass: "preview",
            loadAttr: "data-io-loaded",
            mobileBreakpoint: 768,
            rootMargin: "100px",
            threshold: 0,
        };
        this.root = rootElement || document.body;
        this.init();
    }
    init() {
        if (typeof window === "undefined" || !window.IntersectionObserver) {
            this.loadAllImages();
            this.loadEagerResponsiveIframes();
            return;
        }
        this.setupObserver();
        this.observeImages();
        this.loadEagerResponsiveIframes();
    }
    setupObserver() {
        this.previewObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.intersectionRatio > 0 || entry.isIntersecting) {
                    this.previewObserver?.unobserve(entry.target);
                    if (!this.isLoaded(entry.target)) {
                        this.loadPreviewElement(entry.target);
                    }
                }
            });
        }, {
            rootMargin: this.settings.rootMargin,
            threshold: this.settings.threshold,
        });
    }
    observeImages() {
        const selector = `img.${this.settings.previewClass}, iframe.${this.settings.previewClass}, video.${this.settings.previewClass}`;
        const previews = this.root.querySelectorAll(selector);
        previews.forEach((preview) => {
            if (this.isLoaded(preview)) {
                return;
            }
            if (this.previewObserver) {
                this.previewObserver.observe(preview);
            }
            else {
                this.loadPreviewElement(preview);
            }
        });
    }
    isLoaded(element) {
        return element.hasAttribute(this.settings.loadAttr);
    }
    loadEagerResponsiveIframes() {
        const iframes = this.root.querySelectorAll(`iframe[data-src-video-mobile]:not(.${this.settings.previewClass})`);
        iframes.forEach((iframe) => {
            if (this.isLoaded(iframe) || iframe.getAttribute("src")) {
                return;
            }
            this.loadImageOrIframe(iframe);
        });
    }
    loadPreviewElement(element) {
        if (element instanceof HTMLVideoElement) {
            this.loadVideo(element);
        }
        else if (element instanceof HTMLImageElement || element instanceof HTMLIFrameElement) {
            this.loadImageOrIframe(element);
        }
    }
    loadImageOrIframe(element) {
        const attributes = this.getImageAttributes(element);
        const videoSrc = this.selectVideoSource(attributes);
        const src = videoSrc || this.selectResponsiveSource(attributes);
        if (src) {
            if (videoSrc) {
                element.setAttribute("src", src);
            }
            else {
                const optimizedSrc = this.getOptimizedSource(src, element);
                element.setAttribute("src", optimizedSrc);
            }
            element.setAttribute(this.settings.loadAttr, "true");
        }
    }
    loadVideo(element) {
        applyHeroVideoPoster(element);
        if (applyHeroVideoSource(element)) {
            element.setAttribute(this.settings.loadAttr, "true");
            return;
        }
        const attributes = this.getImageAttributes(element);
        const src = this.selectResponsiveSource(attributes);
        if (src) {
            this.setVideoSource(element, src);
            element.setAttribute(this.settings.loadAttr, "true");
        }
    }
    setVideoSource(videoElement, src) {
        videoElement.querySelectorAll("source").forEach((source) => source.remove());
        videoElement.setAttribute("src", src);
        videoElement.load();
    }
    getImageAttributes(element) {
        return {
            desktop: element.getAttribute("data-src-desktop"),
            mobile: element.getAttribute("data-src-mobile"),
            src: element.getAttribute("data-src"),
            video: element.getAttribute("data-src-video"),
            videoMobile: element.getAttribute("data-src-video-mobile"),
        };
    }
    selectVideoSource(attributes) {
        return selectHeroVideoSource(attributes.video, attributes.videoMobile);
    }
    selectResponsiveSource(attributes) {
        const isMobile = this.isMobileViewport();
        if (attributes.desktop && attributes.mobile) {
            return isMobile ? attributes.mobile : attributes.desktop;
        }
        return attributes.src || attributes.desktop || attributes.mobile;
    }
    isMobileViewport() {
        return window.innerWidth < this.settings.mobileBreakpoint;
    }
    // Cloudflare applies a default quality (~85) when none is set, so a format=auto-only URL is
    // still optimized — but content-reference images use an explicit quality=80. Normalize existing
    // /cdn-cgi/image/ URLs to the same explicit quality for consistent, predictable payloads.
    ensureCloudflareQuality(url, quality = 80) {
        const marker = "/cdn-cgi/image/";
        const markerIndex = url.indexOf(marker);
        if (markerIndex === -1) {
            return url;
        }
        const paramsStart = markerIndex + marker.length;
        const paramsEnd = url.indexOf("/", paramsStart);
        if (paramsEnd === -1) {
            return url;
        }
        const params = url
            .substring(paramsStart, paramsEnd)
            .split(",")
            .map((part) => part.trim())
            .filter(Boolean);
        if (params.some((part) => part.toLowerCase().startsWith("quality="))) {
            return url;
        }
        if (!params.some((part) => part.toLowerCase().startsWith("format="))) {
            params.push("format=auto");
        }
        params.push(`quality=${quality}`);
        return url.substring(0, paramsStart) + params.join(",") + url.substring(paramsEnd);
    }
    shouldUseCloudflare() {
        // Enable Cloudflare CDN when NOT running on localhost
        return typeof window !== 'undefined' &&
            !window.location.hostname.includes('localhost') &&
            !window.location.hostname.includes('127.0.0.1');
    }
    calculateOptimalDimension(element) {
        const bounds = element.getBoundingClientRect();
        if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
            return null;
        }
        const computedStyle = window.getComputedStyle(element);
        const padding = parseFloat(computedStyle.padding) || 0;
        const boundsWidth = Math.round(bounds.width - padding * 2);
        const boundsHeight = Math.round(bounds.height - padding * 2);
        if (boundsWidth <= 1) {
            return null;
        }
        // Get image natural dimensions if available
        const imgElement = element;
        const imgWidth = parseInt(imgElement.getAttribute('width') || '0');
        const imgHeight = parseInt(imgElement.getAttribute('height') || '0');
        // If we have image dimensions, optimize based on aspect ratio
        if (imgWidth > 0 && imgHeight > 0 && boundsHeight > 1) {
            const imageAspect = imgWidth / imgHeight;
            const boundsAspect = boundsWidth / boundsHeight;
            // If image is wider than container, optimize by height to avoid excess width
            // Otherwise, optimize by width
            return imageAspect > boundsAspect
                ? { type: 'height', value: boundsHeight }
                : { type: 'width', value: boundsWidth };
        }
        // Fallback: just use width
        return { type: 'width', value: boundsWidth };
    }
    appendQueryParam(url, param) {
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}${param}`;
    }
    getOptimizedSource(url, element) {
        if (!element)
            return url;
        // Already a Cloudflare image URL (e.g. produced server-side or by the responsive-image
        // pipeline). Don't rebuild it, but make sure quality is present so DAM/richtext images
        // match content-reference images (which always carry quality=80).
        if (url.includes('/cdn-cgi/image/')) {
            return this.ensureCloudflareQuality(url);
        }
        const isCheckDimension = element.hasAttribute("data-smart-dimension");
        const useCloudflare = this.shouldUseCloudflare();
        const CLOUDFLARE_IMAGE_QUALITY = 80;
        if (useCloudflare) {
            // Always use Cloudflare format when not on localhost
            const bounds = element.getBoundingClientRect();
            const params = [`quality=${CLOUDFLARE_IMAGE_QUALITY}`, 'format=auto'];
            // Add width parameter if available
            if (bounds && bounds.width > 0) {
                const computedStyle = window.getComputedStyle(element);
                const padding = parseFloat(computedStyle.padding) || 0;
                const width = Math.round(bounds.width - padding * 2);
                if (isCheckDimension) {
                    const dimension = this.calculateOptimalDimension(element);
                    if (dimension) {
                        params.push(`${dimension.type}=${dimension.value}`);
                    }
                }
                else if (width > 1) {
                    params.push(`width=${width}`);
                }
            }
            // Remove leading slash from the original URL path
            const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
            // Convert to Cloudflare image path format
            url = `/cdn-cgi/image/${params.join(',')}/${cleanUrl}`;
        }
        else {
            // Use standard query parameter for localhost (only if width available)
            const bounds = element.getBoundingClientRect();
            if (bounds && bounds.width > 0) {
                const computedStyle = window.getComputedStyle(element);
                const padding = parseFloat(computedStyle.padding) || 0;
                const width = Math.round(bounds.width - padding * 2);
                if (isCheckDimension) {
                    const dimension = this.calculateOptimalDimension(element);
                    if (dimension) {
                        url = this.appendQueryParam(url, `${dimension.type}=${dimension.value}`);
                    }
                }
                else if (width > 1) {
                    url = this.appendQueryParam(url, `width=${width}`);
                }
            }
        }
        return url;
    }
    loadAllImages() {
        const selector = `img.${this.settings.previewClass}, iframe.${this.settings.previewClass}, video.${this.settings.previewClass}`;
        const previews = this.root.querySelectorAll(selector);
        previews.forEach((preview) => {
            if (!this.isLoaded(preview)) {
                this.loadPreviewElement(preview);
            }
        });
    }
    forceLoadAll() {
        this.loadAllImages();
    }
    destroy() {
        if (this.previewObserver) {
            this.previewObserver.disconnect();
            this.previewObserver = null;
        }
    }
}
export function LoadingImageComponent(element) {
    const instance = new LoadingImage(element);
    return () => {
        instance.destroy();
    };
}
export function preloadPreviewImages(container) {
    const instance = new LoadingImage(container);
    instance.forceLoadAll();
    instance.destroy();
}
